import { useState, type ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import type { S2PElevateShowcase, S2PElevateSlide } from "@/data/s2pElevateAI";

/** One use case, with the evidence path and the human decision visible together. */
export const ElevateShowcaseExample = ({ item, slide, practiceContent }: { item: S2PElevateShowcase; slide: S2PElevateSlide; practiceContent?: ReactNode }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const example = item.example;
  if (!example) return null;

  const showImage = Boolean(item.image && !imageFailed);
  const applications = example.applicationFunctions.flatMap((name) =>
    item.applications.filter((application) => application.function === name),
  );

  return (
    <div data-layout="editorial-showcase-example" data-showcase-id={item.id} className="mx-auto flex min-h-full w-full max-w-[1600px] flex-col justify-center py-4 text-[#0A1F44] sm:py-5">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[#0A1F44]/20 pb-2">
        <p className="text-[11px] font-extrabold uppercase tracking-[.16em] text-[#0047FF]">{slide.eyebrow}</p>
        {item.sourceUrl ? (
          <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0047FF] underline underline-offset-2 focus-visible:outline focus-visible:outline-2">Tool reference · {item.platform}<ExternalLink aria-hidden="true" className="h-3 w-3" /></a>
        ) : <p className="text-[11px] font-semibold text-slate-600">Workshop application · {item.platform}</p>}
      </header>
      <h1 className="s2p-editorial mt-3 max-w-6xl text-[clamp(2rem,3.5vw,3.65rem)] font-bold leading-[1.02] tracking-[-.04em]">{example.title}</h1>

      <div className="mt-4 grid gap-5 md:grid-cols-[1fr_1fr] lg:gap-8">
        <ol aria-label="Workflow and human decision" className="divide-y divide-[#0A1F44]/15 border-y border-[#0A1F44]/15">
          {[
            { title: "Start with", body: example.inputs },
            { title: "AI prepares", body: example.work },
            { title: "A person decides", body: example.review },
          ].map((step, index) => (
            <li key={step.title} className="grid grid-cols-[35px_1fr] gap-3 py-3 lg:grid-cols-[43px_1fr]">
              <span aria-hidden="true" className={`s2p-editorial text-3xl font-bold leading-none ${index === 2 ? "text-[#B84F13]" : "text-[#0047FF]"}`}>0{index + 1}</span>
              <div>
                <h2 className="text-[11px] font-extrabold uppercase tracking-[.12em]">{step.title}</h2>
                <p className="mt-1 text-sm leading-snug text-slate-700 lg:text-base xl:text-lg">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="min-w-0">
          {showImage ? (
            <figure className="border border-[#C8D3E5] bg-white">
              <a href={item.image} target="_blank" rel="noopener noreferrer" aria-label={`Open full vendor screenshot for ${example.title}`} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0047FF]">
                <img src={item.image} alt={item.imageAlt ?? `${item.platform} vendor interface example`} onError={() => setImageFailed(true)} className="h-[clamp(9rem,23svh,15rem)] w-full object-contain" />
              </a>
              <figcaption className="border-t border-[#C8D3E5] px-3 py-2 text-[10px] font-medium text-slate-600">
                <span>Open screenshot for detail · interface may vary</span>
              </figcaption>
            </figure>
          ) : (
            <figure className="border border-[#C8D3E5] bg-white px-4 py-3">
              <figcaption className="flex flex-wrap items-baseline justify-between gap-1 border-b border-[#C8D3E5] pb-2">
                <span className="s2p-editorial text-lg font-semibold">{example.artifact.title}</span>
                <span className="text-[9px] font-bold uppercase tracking-[.1em] text-slate-500">Illustrative output</span>
              </figcaption>
              <ul className="divide-y divide-[#C8D3E5]">
                {example.artifact.lines.map((line) => {
                  const [label, body] = line.split(" · ");
                  return <li key={line} className="py-2"><strong className="block text-[10px] font-extrabold tracking-[.08em] text-[#0047FF]">{label}</strong><span className="mt-0.5 block text-sm leading-snug text-slate-700">{body}</span></li>;
                })}
              </ul>
            </figure>
          )}
          <div className="mt-3 bg-[#0A1F44] px-4 py-3 text-white">
            <h2 className="text-[10px] font-extrabold uppercase tracking-[.14em] text-[#B7CAFF]">What good looks like</h2>
            <p className="mt-1 text-sm font-medium leading-snug lg:text-base">{example.done}</p>
          </div>
        </div>
      </div>

      {practiceContent}

      <aside aria-label="Applications across departments" className="mt-4 border-t border-[#0A1F44]/20 pt-3">
        <div className="grid gap-3 sm:grid-cols-3 sm:gap-5">
          {applications.map((application) => (
            <div key={application.function}>
              <h2 className="text-[10px] font-extrabold uppercase tracking-[.12em] text-[#0047FF]">{application.function}</h2>
              <p className="mt-1 text-xs leading-snug text-slate-600 lg:text-sm">{application.use}</p>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
};
